/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/


// @mui material components
import Container from "@mui/material/Container";
import { Grid, Tooltip } from "@mui/material";
import SoftBadge from "components/SoftBadge";
import SoftTypography from "components/SoftTypography";
import PricingTable from "./components/table";
import CustomizedTables from "./components/table";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EmailIcon from '@mui/icons-material/Email';
import ReplayIcon from '@mui/icons-material/Replay';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';


// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";

// Soft UI Dashboard PRO React example components
import PageLayout from "examples/LayoutContainers/PageLayout";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

// Pricing page components
import Header from "layouts/pages/pricing-page/components/Header";
//import Footer from "layouts/pages/pricing-page/components/Footer";
import PricingCards from "layouts/pages/pricing-page/components/PricingCards";
import TrustedBrands from "layouts/pages/pricing-page/components/TrustedBrands";
import Faq from "layouts/pages/pricing-page/components/Faq";
import Footer from "layouts/authentication/components/Footer";
import { useState, useEffect, useRef } from "react";
import LoadingSpinner from "layouts/pages/projects/recording/components/ringLoader";
import { ClockLoader } from "react-spinners";
import SoftButton from "components/SoftButton";

function PricingPage() {
  const [tabValue, setTabValue] = useState(0);
  const [prices, setPrices] = useState(["89", "129", "169"]);
  const [isLoading, setIsLoading] = useState(false);
  const loaderRef = useRef(null);

  useEffect(() => {
    if (isLoading && loaderRef.current) {
      const loaderPosition = loaderRef.current.getBoundingClientRect().top + window.scrollY;
      const offset = window.innerHeight - loaderRef.current.clientHeight; // Offset to display loader at the bottom
      const additionalMargin = 100;

      window.scrollTo({ top: loaderPosition - offset + additionalMargin, behavior: 'smooth' });
      
    }
  }, [isLoading]);


  const handleSetTabValue = (event, newValue) => {
    setTabValue(newValue);

    if (event.currentTarget.id === "annual") {
      setPrices(["79", "116", "153", "399"]);
    } else {
      setPrices(["89", "129", "169", "999"]);
    }
  };


  
  

  return (
    <DashboardLayout>
      <Header tabValue={tabValue} tabHandler={handleSetTabValue}/>
      <Container >
        <SoftBox mt={10}>
          <PricingCards prices={prices} isLoading={isLoading} setIsLoading={setIsLoading} />
        </SoftBox>
        {isLoading && (
          <SoftBox ref={loaderRef} display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={6} mb={4}>
              <ClockLoader color="#344767" speedMultiplier={0.7} size={80} /> 
                <SoftBadge
                    variant="contained"
                    color="warning"
                    size="md"
                    sx={{mt: 2}}
                    badgeContent={
                        <SoftTypography variant="h6">
                            Just a moment, we are getting your subscription details ready for you!
                        </SoftTypography>
                    }
                />
          </SoftBox>
        )}
        {/*<SoftBox p={2} pt={8} >
          <CustomizedTables />
        </SoftBox>*/}
        <SoftBox pt={5} display="flex" flexDirection="column" justifyContent="center" alignItems="center" >
          <SoftTypography variant="body2" color="dark" >
            📣&nbsp;&nbsp;Looking for <strong>custom solutions or enterprise packages</strong>? Reach out to our sales team below for personalized options tailored to your needs.
          </SoftTypography>
          <Tooltip title="Fill out the form to contact our Sales Team" placement="right" >
          <SoftButton sx={{ mt:4 }} href="https://forms.fillout.com/t/wMXCDMQXDPus" target="_blank" rel="noopener noreferrer" variant="contained" color="dark" >
            Reach out for custom solutions
          </SoftButton>
          </Tooltip>
          <SoftButton sx={{ mt:2 }} variant="outlined" color="dark" >
            Reach out for student discounts (coming soon)
          </SoftButton>
        </SoftBox>

        <SoftBox pt={10} px={5} display="flex" flexDirection="column" justifyContent="flex-start"  >
          <SoftBox bgColor="paper" px={4} display="flex" flexDirection="column" justifyContent="flex-start" sx={{ borderRadius: '10px'}}  >
            <SoftBox ml={0} mb={2} display="flex" alignItems="center" gap={1}>
              <LocalOfferIcon sx={{ color: "silver" }} />
              <SoftTypography variant="h5" >
                Silver Package Information
              </SoftTypography>
            </SoftBox>
            <SoftBox p={2} sx={{ border: '1px solid #344767', borderRadius: "10px", position: 'relative'}} >
              {/*<LocalOfferIcon sx={{ color: "silver", fontSize: 'large', position: 'absolute', top: -10, right: -10 }} />*/}
              <SoftTypography variant="body2" color="dark" >
                Our Silver Package offers a solid foundation for your needs, providing essential services with certain limitations to maintain a cost-effective solution. In this introductory package, you have access to our AI Assistant and can <strong>edit, condense, or delete generated notes</strong>, ensuring you have control over your documentation. This package includes <strong>70 monthly recording hours.</strong>
              </SoftTypography>
              <SoftTypography mt={2} variant="body2" color="dark" >
                Please note that with the Silver Package, <strong>customization options are not available.</strong> This includes the inability to modify default note templates, add new templates, or edit and delete template summaries. Additionally, direct email functionality, note re-generation, and the &apos;talk to your notes&apos; feature are not included. However, you will still benefit from our <strong>basic support</strong> to assist you with any issues or queries.
              </SoftTypography>
              <SoftTypography mt={2} variant="body2" color="dark" >
                For those requiring more flexibility and advanced features, we recommend exploring our Gold and Platinum packages.
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>

        <SoftBox pt={1} px={5} display="flex" flexDirection="column" justifyContent="flex-start"  >
          <SoftBox bgColor="paper" p={4} display="flex" flexDirection="column" justifyContent="flex-start" sx={{ borderRadius: '10px'}}  >
            <SoftBox ml={0} mb={2} display="flex" alignItems="center" gap={1}>
              <LocalOfferIcon sx={{ color: "gold" }} />
              <SoftTypography variant="h5" >
                Gold Package Information
              </SoftTypography>
            </SoftBox>
            <SoftBox p={2} sx={{ border: '1px solid #344767', borderRadius: "10px"}} >
              <SoftTypography mb={4} variant="body2"  >
                Expand your capabilities with our Gold Package, building on the Silver Package&apos;s foundation. This upgrade not only retains all essential services but also enhances your workflow with new features:
              </SoftTypography>
              <List sx={{ width: '100%', bgcolor: 'transparent', color: 'white' }}>
                <ListItem sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ ml: 2 }}>
                    <AccessTimeIcon color='dark'  />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <SoftTypography variant="button" >
                        120 monthly recording hours, providing ample room for extensive patient documentation.
                      </SoftTypography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ ml: 2 }}>
                    <EditIcon color='dark'  />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <SoftTypography variant="button" >
                        Ability to edit default templates and add up to 3 personal templates, offering customization to suit your needs.
                      </SoftTypography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ ml: 2 }}>
                    <NoteAddIcon color='dark'  />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <SoftTypography variant="button" >
                        Enhanced template management, including the option to delete a summary or the entire template.
                      </SoftTypography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ ml: 2 }}>
                    <EmailIcon color='dark'  />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <SoftTypography variant="button" >
                        Generate and send 1 customizable email to patients per recording, enhancing communication.
                      </SoftTypography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ ml: 2 }}>
                    <ReplayIcon color='dark'  />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <SoftTypography variant="button" >
                        Access to the &apos;re-generating feature&apos; and &apos;talk to your notes&apos; feature, adding flexibility to your note management.
                      </SoftTypography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ ml: 2 }}>
                    <SupportAgentIcon color='dark' />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <SoftTypography variant="button" >
                        Premium support, ensuring top-tier assistance whenever you need it.
                      </SoftTypography>
                    }
                  />
                </ListItem>
              </List>

              <SoftTypography mt={4} variant="body2"  >
                Our Gold Package is designed for healthcare professionals seeking more flexibility and control over their patient management system. By offering these advanced features, we aim to enhance your productivity, allowing you to focus more on patient care and less on administrative tasks.              
              </SoftTypography>
              <SoftTypography mt={2} variant="body2"  >
                For those looking to maximize their Optimind-AI experience with even more sophisticated tools and capabilities, our Platinum Package may be the perfect fit. Explore the boundaries of what our platform can offer and elevate your practice to new heights.
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>

        <SoftBox pt={1} px={5} display="flex" flexDirection="column" justifyContent="flex-start"  >
          <SoftBox bgColor="paper" px={4} display="flex" flexDirection="column" justifyContent="flex-start" sx={{ borderRadius: '10px'}}  >
            <SoftBox ml={0} mb={2} display="flex" alignItems="center" gap={1}>
              <LocalOfferIcon sx={{ color: "platinum" }} />
              <SoftTypography variant="h5" >
                Platinum Package Information
              </SoftTypography>
            </SoftBox>
            <SoftBox p={2} sx={{ border: '1px solid #344767', borderRadius: "10px"}} >
              <SoftTypography variant="body2" color="dark" >
                Elevate your practice to unparalleled heights with our Platinum Package, where limits are a thing of the past, and ultimate flexibility meets cutting-edge functionality. This premium package is meticulously designed for those who demand nothing but the best, offering an expansive <strong>250 monthly recording hours</strong> to accommodate extensive patient documentation needs effortlessly.              
              </SoftTypography>
              <SoftTypography mt={2} variant="body2" color="dark" >
                With the Platinum Package, every feature and capability of our platform is at your disposal, unrestricted and infinitely customizable. From editing default templates to adding an unlimited number of personal templates, the power to tailor your workflow is in your hands. This package removes all boundaries, allowing you to manage, condense, or delete your notes and templates with <strong>complete freedom</strong>, providing the ultimate flexibility to cater to your specific needs.              </SoftTypography>
              <SoftTypography mt={2} variant="body2" color="dark" >
                Communication with patients is enhanced through the ability to generate and send <strong>unlimited customizable emails per recording</strong>, ensuring you maintain a continuous connection with your patients. The Platinum Package also introduces access to <strong>MDM (Medical Decision Making) coding</strong>, streamlining your administrative tasks with effortless generation of these crucial code notes. 
              </SoftTypography>
              <SoftTypography mt={2} variant="body2" color="dark" >
                But the exclusivity of the Platinum Package doesn&apos;t end there. You will benefit from our <strong>Ultimate Support Tier</strong>, the highest level of assistance we offer. This includes priority response, dedicated support, and personalized consultations, ensuring your experience with our platform is seamless, efficient, and precisely tailored to your needs.
              </SoftTypography>
              <SoftTypography mt={2} variant="body2" color="dark" >
                Choose the Platinum Package for an experience that transcends the conventional, allowing you to maximize your practice&apos;s efficiency, personalize your patient care, and utilize the full scope of Optimind-AI&apos;s capabilities. With these sophisticated tools and unparalleled support at your disposal, the Platinum Package is here to transform your patient documentation and communication, unlocking a new realm of possibilities for your practice.
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>

        <SoftBox pt={10} pl={2} display="flex" flexDirection="column" justifyContent="center" alignItems="center" >
          <SoftBadge
              variant="contained"
              color="warning"
              size="md"
              badgeContent={
                  <SoftTypography variant="h5">
                      How Our Support System Works
                  </SoftTypography>
              }
          />
          <SoftTypography px={10} pt={6} variant="body2" >
            Our support system is designed to provide you with assistance whenever you need it, directly from within the Optimind-AI application. Our team offers 24/7 support, ensuring that help is always just a few clicks away. Here&apos;s how our support tiers work:
          </SoftTypography>
          <List sx={{ width: '100%', bgcolor: 'transparent', color: 'white', px: 10 }}>
            <ListItem sx={{ mb: 1, mt: 4 }}>
              <ListItemIcon sx={{ ml: 0 }}>
                <Tooltip title="Basic Support" placement="left" >
                <LocalOfferIcon color='silver'  />
                </Tooltip>
              </ListItemIcon>
              <ListItemText 
                primary={
                  <SoftTypography variant="body2" >
                    <strong>Basic Support:</strong> Available with the Silver Package, our team aims to respond within 3-4 hours during our office hours, which are from 6 AM to 8 PM Arizona time, Monday through Sunday. Outside of these hours, we&apos;re still here to assist, although we can&apos;t guarantee these specific response times.
                  </SoftTypography>
                }
              />
            </ListItem>
            <ListItem sx={{ mb: 1, mt: 4 }}>
              <ListItemIcon sx={{ ml: 0 }}>
                <Tooltip title="Premium Support" placement="left" >
                <LocalOfferIcon sx={{ color:'gold'}}  />
                </Tooltip>
              </ListItemIcon>
              <ListItemText 
                primary={
                  <SoftTypography variant="body2" >
                    <strong>Premium Support:</strong> Included with the Gold Package, this level of support guarantees a response time of 2-3 hours during office hours. Our commitment to quick and effective support means less waiting for you.
                  </SoftTypography>
                }
              />
            </ListItem>
            <ListItem sx={{ mb: 1, mt: 4 }}>
              <ListItemIcon sx={{ ml: 0 }}>
                <Tooltip title="Ultimate Support" placement="left" >
                <LocalOfferIcon color='dark'  />
                </Tooltip>
              </ListItemIcon>
              <ListItemText 
                primary={
                  <SoftTypography variant="body2" >
                    <strong>Ultimate Support:</strong> Our highest tier of support comes with the Platinum Package, offering an impressive response time of 1-2 hours during office hours. This ensures that any issues or questions you have are addressed as quickly as possible.
                  </SoftTypography>
                }
              />
            </ListItem>
          </List>
          <SoftTypography px={10} pt={3} variant="body2" >
            Remember, no matter the time of day or night, our support team is always on standby to assist you. However, please note that specific response times can vary outside of office hours. Our goal is to provide you with the support you need, whenever you need it, to make your experience with Optimind-AI as smooth and efficient as possible.
          </SoftTypography>
        </SoftBox>

        <Faq />
      </Container>
      <SoftBox mt={4}>
        <Footer />
      </SoftBox>
    </DashboardLayout>
  );
}

export default PricingPage;
